/**
 * Stroke-only icon set used by the rails.
 *
 * We ship our own minimal set so the handoff has zero icon-library
 * dependency. Swap for ionicons / your in-house set at the call site
 * by passing `icon` (ReactNode) on a NavItem.
 */

import type { ReactNode } from "react";

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const Svg = ({ children, size = 18 }: { children: ReactNode; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    {children}
  </svg>
);

export const NavIcons = {
  /* --- Customer CRM rail --- */
  inbox: (
    <Svg>
      <path d="M3 13h4l2 3h6l2-3h4" />
      <path d="M5 5h14l2 8v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6z" />
    </Svg>
  ),
  calls: (
    <Svg>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7a2 2 0 0 1 1.72 2z" />
    </Svg>
  ),
  leads: (
    <Svg>
      <path d="M3 5h18l-7 8v6l-4 2v-8z" />
    </Svg>
  ),
  admissions: (
    <Svg>
      <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" />
    </Svg>
  ),
  reports: (
    <Svg>
      <path d="M3 20h18" />
      <rect x="5" y="11" width="3" height="7" />
      <rect x="11" y="6" width="3" height="12" />
      <rect x="17" y="13" width="3" height="5" />
    </Svg>
  ),
  settings: (
    <Svg>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </Svg>
  ),

  /* --- UI affordances --- */
  chevronDown: (
    <Svg size={12}>
      <polyline points="6 9 12 15 18 9" />
    </Svg>
  ),
  chevronLeft: (
    <Svg size={14}>
      <polyline points="15 18 9 12 15 6" />
    </Svg>
  ),
  chevronRight: (
    <Svg size={14}>
      <polyline points="9 18 15 12 9 6" />
    </Svg>
  ),
  check: (
    <Svg size={14}>
      <polyline points="20 6 9 17 4 12" />
    </Svg>
  ),
  search: (
    <Svg size={14}>
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.65" y2="16.65" />
    </Svg>
  ),
  shieldStar: (
    <Svg size={12}>
      <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" />
    </Svg>
  ),
  doorExit: (
    <Svg size={14}>
      <path d="M16 17l5-5-5-5" />
      <line x1="21" y1="12" x2="9" y2="12" />
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    </Svg>
  ),
};
